import { Card, Inline, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMembers } from "../members/useMembers.js";
import { useAdminNav } from "../lib/adminNav.js";
import { AdminQuickActionsCard } from "./AdminQuickActionsCard.js";
// GT-GFI-ADMIN-COPILOT-RISK-RADAR-SHELL-01 (Sprint E) — Platform-Owner-only
// deterministic Customer Risk Radar card (renders nothing for others).
import { AdminRiskRadarCard } from "./AdminRiskRadarCard.js";
import { formatCount } from "../locale-format.js";

/**
 * Admin overview — landing page of the admin console.
 *
 * Reads the same `/v1/admin/members` endpoint as the members list
 * (no separate summary endpoint yet; total roster is small enough
 * to compute client-side). Cards intentionally show only what the
 * read model can back with real numbers; anything speculative would
 * add trust debt without user value.
 */
export function OverviewPage() {
  const { t, locale } = useLocale();
  const { navigateTo } = useAdminNav();
  const query = useMembers({ limit: 100 });
  const data = query.data;

  const rolesInUse = data ? Object.entries(data.byRole).filter(([, n]) => n > 0).length : undefined;
  const members = data?.total;
  const activeMembers = data?.byStatus.active;

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <h1
          style={{
            fontSize: "var(--gt-sys-font-size-2xl)",
            fontWeight: "var(--gt-sys-font-weight-bold)",
            margin: 0,
          }}
        >
          {t("admin.overview.title")}
        </h1>
        <p style={{ color: "var(--gt-sys-text-muted)", margin: 0 }}>
          {t("admin.overview.subtitle")}
        </p>
      </Stack>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "var(--gt-sys-space-3)",
        }}
      >
        <StatCard
          label={t("admin.overview.members.label")}
          hint={t("admin.overview.members.hint")}
          value={members === undefined ? "…" : formatCount(members, locale)}
        />
        <StatCard
          label={t("admin.overview.active.label")}
          hint={t("admin.overview.active.hint")}
          value={activeMembers === undefined ? "…" : formatCount(activeMembers, locale)}
        />
        <StatCard
          label={t("admin.overview.roles.label")}
          hint={t("admin.overview.roles.hint")}
          value={rolesInUse === undefined ? "…" : formatCount(rolesInUse, locale)}
        />
      </div>

      {/* Sprint 26.5 Wave B — admin Quick Actions card. Gated by
          sprint26.adminQuickActions; pure navigation only. */}
      <AdminQuickActionsCard />

      {/* GT-GFI-ADMIN-COPILOT-RISK-RADAR-SHELL-01 (Sprint E) — Platform-Owner
          only; renders nothing for other roles. Read-only, deterministic.
          GT-GFI-OWNER-SMOKE-FIX-RISK-RADAR-VISIBILITY-01 — the card now also
          links to the dedicated "AI Copilot" page so the Owner can reach the
          full Risk Radar + Delivery Health from the landing page. */}
      <AdminRiskRadarCard onOpenFullView={() => navigateTo("ai_copilot")} />
    </Stack>
  );
}

function StatCard(props: { label: string; hint: string; value: string }) {
  return (
    <Card elevation={1}>
      <Stack gap={1}>
        <Inline gap={2} justify="space-between" align="center">
          <span
            style={{
              fontSize: "var(--gt-sys-font-size-xs)",
              color: "var(--gt-sys-text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {props.label}
          </span>
        </Inline>
        <span
          style={{
            fontSize: "var(--gt-sys-font-size-2xl)",
            fontWeight: "var(--gt-sys-font-weight-bold)",
          }}
        >
          {props.value}
        </span>
        <span style={{ fontSize: "var(--gt-sys-font-size-xs)", color: "var(--gt-sys-text-muted)" }}>
          {props.hint}
        </span>
      </Stack>
    </Card>
  );
}
